import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  APPROVED,
  AUTHORIZATIONS,
  CORPORATE,
  APP_ONLY_CORRIDOOR_WELCOME_URL,
  HK,
  INDIVIDUAL,
  MANAGE_PAYMENT_OPTION_URL,
  REFERRAL_URL,
  SUBSCRIPTION_URL,
  USER_PROFILE_URL,
  SG,
  UK,
  US,
  AU,
  EU_REGION,
  BASE_CDN_CONSUMER_URL
} from '@Utils/constants';
import { shouldShowPaymentOptionTab } from '@Utils/library';
import { selectUserSession, selectUserStatusReducer } from '@Redux/selectors';
import { MobileMenuItem } from '../SideNav.types';

interface NonHardMigratedSMEFlow {
  isFeatureEnabled: boolean;
  data: string[];
}

interface UserStatusWithSme {
  status: string;
  country_code: string;
  nonHardMigratedSMEFlow?: NonHardMigratedSMEFlow;
  [key: string]: any;
}

interface UserSessionWithSme {
  account_type: string;
  additional_info?: {
    hard_sme_migrated?: boolean;
    [key: string]: any;
  };
  [key: string]: any;
}

const SHOW_MANAGE_PAYMENT = [HK, US, SG, UK, AU, ...EU_REGION];
const SHOW_MANAGE_PAYMENT_CORPORATE = [US];

export const useMobileMenuItems = (): MobileMenuItem[] => {
  const { status, country_code, nonHardMigratedSMEFlow } = useSelector(
    selectUserStatusReducer
  ) as UserStatusWithSme;
  const { account_type, additional_info } = useSelector(selectUserSession) as UserSessionWithSme;

  const hard_sme_migrated = account_type === CORPORATE && additional_info?.hard_sme_migrated;
  const isAppOnlyCorridorRoute = window.location.href.includes(APP_ONLY_CORRIDOOR_WELCOME_URL);
  const paymentOptionTabVisible = shouldShowPaymentOptionTab(country_code);

  let smeFlow = false;
  if (nonHardMigratedSMEFlow?.isFeatureEnabled && account_type === CORPORATE) {
    const { data } = nonHardMigratedSMEFlow;
    smeFlow = data.includes(country_code) && status === APPROVED;
  }

  const showAccountLinks = !isAppOnlyCorridorRoute && !(hard_sme_migrated || smeFlow);
  const profileAuthorized = Boolean(
    (AUTHORIZATIONS as any)[status] &&
      (AUTHORIZATIONS as any)[status].find((text: string) => text === USER_PROFILE_URL)
  );
  const showManagePaymentOption =
    status === APPROVED &&
    ((SHOW_MANAGE_PAYMENT.includes(country_code) && account_type === INDIVIDUAL) ||
      (SHOW_MANAGE_PAYMENT_CORPORATE.includes(country_code) && account_type === CORPORATE)) &&
    paymentOptionTabVisible;
  const showReferral = status !== APPROVED && account_type !== CORPORATE;

  return useMemo(() => {
    if (!showAccountLinks) return [];
    const items: MobileMenuItem[] = [
      {
        key: 'my-info',
        label: 'My info',
        icon: `${BASE_CDN_CONSUMER_URL}web-revamp/profile-icon.svg`,
        route: USER_PROFILE_URL,
        disabled: !profileAuthorized
      }
    ];
    if (showManagePaymentOption) {
      items.push({
        key: 'manage-payment',
        label: 'Manage payment options',
        icon: `${BASE_CDN_CONSUMER_URL}web-revamp/manage-pyament-icon.svg`,
        route: MANAGE_PAYMENT_OPTION_URL
      });
    }
    items.push({
      key: 'subscription',
      label: 'Subscription',
      icon: `${BASE_CDN_CONSUMER_URL}web-revamp/subscription-icon.svg`,
      route: SUBSCRIPTION_URL
    });
    if (showReferral) {
      items.push({
        key: 'referral',
        label: 'Referrals',
        icon: `${BASE_CDN_CONSUMER_URL}web-revamp/add-user-icon.svg`,
        route: REFERRAL_URL
      });
    }
    return items;
  }, [showAccountLinks, profileAuthorized, showManagePaymentOption, showReferral]);
};
